import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { parentApi } from "../lib/api";
import { CheckCircle2, XCircle, Clock, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Landing page after an HDFC SmartGateway redirect. The backend has already
// confirmed + settled the payment via the Order Status API before redirecting
// here with ?order=<id>&result=success|failed|pending.
export default function PaymentStatus() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const order = params.get("order") || "";
  const result = params.get("result") || "pending";
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!order) { setLoading(false); return; }
      try {
        const { data } = await parentApi.get(`/payments/status/${order}`);
        if (alive) setInfo(data);
      } catch {
        /* fall back to the result param */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [order]);

  const paid = result === "success" || info?.status === "paid";
  const pending = !paid && result === "pending";

  const downloadReceipt = async () => {
    if (!info?.receipt_id) return;
    try {
      const res = await parentApi.get(`/parent/receipts/${info.receipt_id}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `naavi-receipt-${String(info.receipt_no || info.receipt_id).replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download receipt. Please open it from the portal.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1B47]/5 flex items-center justify-center p-4">
      <div className="bg-[#FDFDF9] rounded-[28px] w-full max-w-md shadow-2xl overflow-hidden p-8 text-center" data-testid="payment-status">
        {loading ? (
          <div className="py-10 text-[#718096] text-sm">Confirming your payment…</div>
        ) : paid ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#5BC4B8] text-[#0F1B47] flex items-center justify-center mb-4">
              <CheckCircle2 size={26} />
            </div>
            <h1 className="font-display text-2xl text-[#0F1B47] font-medium">Payment successful</h1>
            <p className="text-sm text-[#4A5568] mt-2">
              {info?.amount ? <>We've recorded <b className="text-[#0F1B47]">{INR(info.amount)}</b>. </> : null}
              Thank you.
            </p>
            <div className="mt-5 inline-flex flex-col items-start gap-2 bg-[#F5F5F0] rounded-2xl px-5 py-3 text-left">
              {order && (
                <div>
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Order number</div>
                  <div className="font-mono text-sm font-semibold text-[#0F1B47]" data-testid="ps-order">{order}</div>
                </div>
              )}
              {info?.amount != null && (
                <div>
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Amount paid</div>
                  <div className="font-mono text-sm font-semibold text-[#0F1B47]" data-testid="ps-amount">{INR(info.amount)}</div>
                </div>
              )}
              {info?.receipt_no && (
                <div>
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Receipt</div>
                  <div className="font-mono text-sm font-semibold text-[#0F1B47]">{info.receipt_no}</div>
                </div>
              )}
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3">
              {info?.receipt_id && (
                <button onClick={downloadReceipt} className="inline-flex items-center justify-center gap-1.5 bg-[#C6D947] text-[#0F1B47] rounded-full py-3 font-semibold text-sm">
                  <Download size={14} /> Download receipt
                </button>
              )}
              <button onClick={() => navigate("/parent")} className="inline-flex items-center justify-center gap-1.5 bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 font-semibold text-sm hover:bg-[#1a2b6b]">
                Back to portal <ArrowRight size={14} />
              </button>
            </div>
          </>
        ) : pending ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
              <Clock size={26} />
            </div>
            <h1 className="font-display text-2xl text-[#0F1B47] font-medium">Payment processing</h1>
            <p className="text-sm text-[#4A5568] mt-2">
              Your payment is still being confirmed by the bank. If money was debited, it will reflect shortly — please check the portal in a few minutes before retrying.
            </p>
            <button onClick={() => navigate("/parent")} className="mt-6 inline-flex items-center justify-center gap-1.5 bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 px-6 font-semibold text-sm hover:bg-[#1a2b6b]">
              Back to portal <ArrowRight size={14} />
            </button>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-4">
              <XCircle size={26} />
            </div>
            <h1 className="font-display text-2xl text-[#0F1B47] font-medium">Payment not completed</h1>
            <p className="text-sm text-[#4A5568] mt-2">
              The payment didn't go through. No money has been deducted, or any debit will be auto-refunded. You can try again from the portal.
            </p>
            <button onClick={() => navigate("/parent")} className="mt-6 inline-flex items-center justify-center gap-1.5 bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 px-6 font-semibold text-sm hover:bg-[#1a2b6b]">
              Back to portal <ArrowRight size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
